"use client";

import { useMemo } from "react";

const DATA_URL_PREFIX = "data:";
const BASE64_MARKER = ";base64,";

const getMimeType = (content: string): string | null => {
  if (!content.startsWith(DATA_URL_PREFIX)) {
    return null;
  }

  const endOfMime = content.indexOf(BASE64_MARKER);
  if (endOfMime === -1) {
    return null;
  }

  return content.slice(DATA_URL_PREFIX.length, endOfMime);
};

// MIME types that look text-ish (e.g. contain "xml") but are really binary payloads.
// Keep this list conservative — only add entries with a known base64-delivered binary.
const BINARY_APPLICATION_MIME_PREFIXES = [
  "application/vnd.", // Office (docx/xlsx/pptx) and other vendor-specific binaries
  "application/msword",
  "application/zip",
  "application/x-zip",
  "application/octet-stream",
];

const isTextLikeMimeType = (mimeType: string): boolean => {
  // `image/svg+xml` contains "xml" but must still use image preview, not CSV/text parsing.
  if (mimeType.startsWith("image/")) {
    return false;
  }

  if (BINARY_APPLICATION_MIME_PREFIXES.some(prefix => mimeType.startsWith(prefix))) {
    return false;
  }

  return (
    mimeType.startsWith("text/") ||
    mimeType.includes("json") ||
    mimeType.includes("xml") ||
    mimeType.includes("yaml")
  );
};

type BinaryPreviewKind = "image" | "video" | "audio" | "pdf" | "unknown";

const buildDetectedFile = (
  content: string,
): { type: BinaryPreviewKind; mimeType: string; extension?: string } | null => {
  const mimeType = getMimeType(content);
  if (!mimeType) {
    return null;
  }

  const extension = mimeType.split("/")[1]?.split("+")[0] ?? undefined;

  if (mimeType.startsWith("image/")) {
    return { type: "image", mimeType, extension };
  }

  if (mimeType.startsWith("video/")) {
    return { type: "video", mimeType, extension };
  }

  if (mimeType.startsWith("audio/")) {
    return { type: "audio", mimeType, extension };
  }

  if (mimeType === "application/pdf") {
    return { type: "pdf", mimeType, extension };
  }

  return { type: "unknown", mimeType, extension };
};

export const isBinarySkillFileContent = (content: string): boolean => {
  if (!content.startsWith(DATA_URL_PREFIX) || !content.includes(BASE64_MARKER)) {
    return false;
  }

  const mimeType = getMimeType(content);
  if (!mimeType) {
    return true;
  }

  return !isTextLikeMimeType(mimeType);
};

export const BinarySkillFilePreview = ({
  content,
  compact = false,
}: {
  content: string;
  compact?: boolean;
}) => {
  const detectedFile = useMemo(() => buildDetectedFile(content), [content]);

  if (!detectedFile) {
    return null;
  }

  if (detectedFile.type === "image") {
    return (
      <div
        className={`flex h-full w-full items-center justify-center bg-gray-50 ${
          compact ? "p-3" : "p-6"
        }`}
      >
        <img
          src={content}
          alt="Skill file preview"
          className={`max-w-full rounded-lg object-contain shadow-sm ${
            compact ? "max-h-[320px]" : "max-h-full"
          }`}
        />
      </div>
    );
  }

  if (detectedFile.type === "video") {
    return (
      <div
        className={`flex h-full w-full items-center justify-center bg-gray-50 ${
          compact ? "p-3" : "p-6"
        }`}
      >
        <video
          src={content}
          controls
          className={`max-w-full rounded-lg object-contain shadow-sm ${
            compact ? "max-h-[320px]" : "max-h-full"
          }`}
        >
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  if (detectedFile.type === "audio") {
    return (
      <div
        className={`flex h-full w-full items-center justify-center bg-gray-50 ${
          compact ? "p-3" : "p-6"
        }`}
      >
        <div className="w-full max-w-2xl rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <audio src={content} controls className="w-full">
            Your browser does not support the audio tag.
          </audio>
        </div>
      </div>
    );
  }

  if (detectedFile.type === "pdf") {
    return (
      <div className={`flex h-full w-full bg-gray-50 ${compact ? "p-3" : "p-6"}`}>
        <embed
          src={content}
          type="application/pdf"
          className={`h-full w-full rounded-lg border border-gray-200 bg-white shadow-sm ${
            compact ? "min-h-[420px]" : "min-h-[70vh]"
          }`}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex h-full w-full items-center justify-center bg-gray-50 ${
        compact ? "p-3" : "p-6"
      }`}
    >
      <div className="w-full max-w-2xl rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600 shadow-sm">
        Preview unavailable for this file type.
      </div>
    </div>
  );
};
